// ClipRadar website — testimonials, desktop app, why-bento.
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CRWIcon, type IconName } from '../components/icons';
import { AppFrame, AppSearchBox, MetalBtn, ResultCard, ResultGroup, SectionHead } from '../components/primitives';
import { STILLS } from '../components/stills';
import { useProductTilt } from '../components/use-product-tilt';

gsap.registerPlugin(ScrollTrigger);

// Neutral icon tile — the A1 chrome-neutral variant of IconTile: same glass
// plate, fg-2 stroke icon, no warm halo (marketing icons are neutral chrome now).
function NeutralTile({ d, size = 38 }: { d: IconName; size?: number }) {
  return (
    <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 11, background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.12)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 20px rgba(0,0,0,0.4)', color: 'var(--fg-2)' }}>
      <CRWIcon d={d} size={Math.round(size * 0.46)} />
    </span>
  );
}

/* ---------- BETA TESTIMONIALS — featured quote + grid ---------- */
type Quote = { q: string; n: string; r: string; tag: string };

const QUOTES: Quote[] = [
  { q: 'I used to have to constantly search my folders on old projects for footage I knew would be useful. Now I just have this library, which gets built with every project, and I just search. It’s made my work so much faster.', n: 'Chris D.', r: 'Faceless channel editor', tag: 'ONE GROWING LIBRARY' },
  { q: 'I pull b-roll from a massive archive for every video. I describe the shot I’ve got in my head and it surfaces — no more scrubbing folders by filename.', n: 'Martin K.', r: 'Long-form YouTube editor', tag: 'VISUAL SEARCH' },
  { q: 'I knew the exact line was buried somewhere in hours of footage. Typed it, landed on the frame. That used to eat my whole afternoon.', n: 'Lena H.', r: 'YouTube video editor', tag: 'SPEECH' },
  { q: 'Name a person once and every clip they’re in shows up across all my drives. For anything with recurring people that’s huge.', n: 'Georgi V.', r: 'Freelance editor', tag: 'FACES' },
  { q: 'It’s a beta and you can feel it in spots — but the search itself is the real deal. It keeps surfacing clips I’d never have found by hand.', n: 'Rado G.', r: 'Faceless channel editor', tag: 'HONEST TAKE' },
  { q: 'The panel feels like part of Premiere, not a thing bolted on. Search, click, and it’s on the timeline with in and out points already set.', n: 'Tomasz W.', r: 'Video editor', tag: 'PREMIERE PANEL' },
];

// The breather replaces the big Testimonials section (2026-07-17): Jumper's
// rhythm, ClipRadar's clothes — one verbatim beta quote between the proof,
// centered, framed by IN/OUT ticks like a select, a monogram disc instead of a
// stock face (we don't fake faces), one faint glacier pool as the lit moment.
export function Breather({ name }: { name: string }) {
  const quote = QUOTES.find((q) => q.n === name) ?? QUOTES[0];
  const initials = name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <section className="crw-qb">
      <span className="crw-qb-pool" aria-hidden="true"></span>
      <span className="crw-qb-eb">From the closed beta</span>
      <div className="crw-qb-wrap">
        <span className="crw-qb-tick crw-tin" aria-hidden="true"><i>IN</i></span>
        <blockquote>“{quote.q}”</blockquote>
        <span className="crw-qb-tick crw-tout" aria-hidden="true"><i>OUT</i></span>
      </div>
      <div className="crw-qb-who">
        <span className="crw-qb-disc" aria-hidden="true">{initials}</span>
        <span className="crw-qb-nm">{quote.n}</span>
        <span className="crw-qb-rl">· {quote.r}</span>
      </div>
    </section>
  );
}

// The big proof moment, restored 2026-07-17 (dissolving it into breathers was
// wrong — breathers were meant to be ADDED, not replace it). One featured
// pull-quote set large, then the three search modes as a single hairline log
// row, then the beta-membership band. Tomasz + Sofia live in standalone
// Breathers elsewhere on the page, so no quote appears twice.
export function Testimonials() {
  const featured = QUOTES[0];
  const log = QUOTES.slice(1, 4);
  const perks = [
    'A new build every week — members vote what ships next',
    '1-on-1 support, straight from the team',
    'Your workflow shapes the product',
  ];
  return (
    <section style={{ padding: '90px 0', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '-220px', transform: 'translateX(-50%)', width: 1000, height: 700, background: 'radial-gradient(ellipse, rgba(var(--light-rgb),0.07), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ padding: '0 24px' }}>
        <SectionHead center label="Early testers" title="What early testers are saying." />
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px' }}>
        {/* featured pull-quote — no box; the hanging glyph is the section's one flourish */}
        <figure className="crw-pullquote" style={{ margin: 0, position: 'relative', paddingLeft: 64 }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: -4, top: -16, fontFamily: 'var(--font-display)', fontSize: 100, lineHeight: 1, fontWeight: 700, color: 'var(--fg-decor)' }}>“</span>
          <blockquote style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(21px, 2.3vw, 28px)', fontWeight: 600, letterSpacing: 'var(--tracking-heading)', lineHeight: 1.45, color: 'var(--fg-1)', textWrap: 'pretty' }}>
            {featured.q}
          </blockquote>
          <figcaption style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', color: 'var(--fg-2)' }}>
              {featured.n} <i style={{ fontStyle: 'normal', color: 'var(--fg-3)' }}>— {featured.r}</i>
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, letterSpacing: '0.14em', color: 'var(--fg-3)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line-1)', padding: '3px 8px', borderRadius: 999 }}>{featured.tag}</span>
          </figcaption>
        </figure>

        {/* the log — one hairline-ruled row: the three search modes speaking */}
        <div className="crw-quote-log3" style={{ marginTop: 48, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {log.map((c) => (
            <figure key={c.n} className="crw-log3-entry" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ alignSelf: 'flex-start', fontFamily: 'var(--font-mono)', fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{c.tag}</span>
              <blockquote style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--fg-2)', textWrap: 'pretty' }}>“{c.q}”</blockquote>
              <figcaption style={{ marginTop: 'auto', fontSize: 12, color: 'var(--fg-1)', fontWeight: 500 }}>
                {c.n} <i style={{ fontStyle: 'normal', color: 'var(--fg-3)', fontWeight: 400 }}>· {c.r}</i>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {/* beta membership — an open band bounded by rules, not another floating box */}
      <div style={{ maxWidth: 1120, margin: '72px auto 0', padding: '0 24px' }}>
        <div className="crw-beta-band" style={{ borderTop: '1px solid var(--line-2)', borderBottom: '1px solid var(--line-2)', padding: '36px 4px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <b style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)', textWrap: 'balance' }}>Beta members steer the roadmap.</b>
            <div className="crw-perk-row" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {perks.map((p) => (
                <span key={p} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 9, fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                  <span style={{ color: 'var(--fg-3)', display: 'inline-flex', transform: 'translateY(1px)' }}><CRWIcon d="check" size={11} sw={2.6} /></span>{p}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11 }}>
            <Link to="/trial" style={{ textDecoration: 'none', display: 'flex' }}>
              <MetalBtn size="lg" style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Start Free Trial</MetalBtn>
            </Link>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--fg-3)', textTransform: 'uppercase', textAlign: 'center' }}>
              40 free searches · no credit card required
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- DESKTOP APP — the real app, full library view ---------- */
function DesktopAppMock() {
  // top rows: every angle of the Apollo 11 launch (the archival demo library —
  // per-still filenames + timecodes match the hero/scenes); last card: a
  // SECOND real launch (Mercury-Redstone) instead of a looser match — every
  // card is a genuine launch angle
  const files = [
    { f: 'apollo11-pad-39a.mp4', img: STILLS.launchpad, pos: '50% 45%', zoom: 1, tc: '00:00:05 - 00:00:12', dur: '7s' },
    { f: 'apollo11-launch-1969.mp4', img: STILLS.padflame, pos: '50% 45%', zoom: 1, tc: '00:00:21 - 00:00:24', dur: '3s' },
    { f: 'apollo11-liftoff-1969.mp4', img: STILLS.ignition, pos: '50% 50%', zoom: 1, tc: '00:00:24 - 00:00:29', dur: '5s' },
    { f: 'apollo11-launch-1969.mp4', img: STILLS.padsmoke, pos: '50% 45%', zoom: 1, tc: '00:00:26 - 00:00:30', dur: '4s' },
    { f: 'apollo11-liftoff-1969.mp4', img: STILLS.climb, pos: '50% 40%', zoom: 1, tc: '00:00:33 - 00:00:36', dur: '3s' },
    { f: 'apollo11-tracking-cam.mp4', img: STILLS.liftoff, pos: '50% 35%', zoom: 1, tc: '00:00:33 - 00:00:38', dur: '5s' },
    { f: 'apollo11-tracking-cam.mp4', img: STILLS.tracking, pos: '50% 40%', zoom: 1, tc: '00:00:39 - 00:00:44', dur: '5s' },
    { f: 'mercury-redstone-launch-1960.mp4', img: STILLS.redstoneliftoff, pos: '50% 45%', zoom: 1, tc: '00:08:05 - 00:08:18', dur: '13s' },
  ];
  return (
    <div style={{ height: 540 }}>
      <AppFrame tab="Search" badge="INDEXING NAS · 31%">
        <AppSearchBox query="every angle of the launch" history={['the rocket on the pad', 'crowds at the parade']} compact />
        {/* results toolbar — count, thumbnail zoom, export, clear */}
        <div className="crw-fade" style={{ animationDelay: '2400ms', display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--fg-3)', whiteSpace: 'nowrap', flex: 'none' }}>1 search</span>
          <span className="crw-zoom" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-4)' }}>
            <CRWIcon d="grid" size={11} />
            <span style={{ width: 70, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.12)', position: 'relative' }}>
              <i style={{ position: 'absolute', left: '42%', top: -3.5, width: 10, height: 10, borderRadius: '50%', background: '#D4D4D8' }}></i>
            </span>
          </span>
          <span style={{ flex: 1 }}></span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--fg-2)', border: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap', flex: 'none' }}><CRWIcon d="downToLine" size={11} /> Export to timeline</span>
          <span className="crw-clearall" style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--fg-3)', border: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>Clear all results</span>
        </div>
        <ResultGroup query="every angle of the launch" files={6} delay={2700}>
          <div className="crw-grid4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {files.map((c, i) => (
              <ResultCard key={i} img={c.img} pos={c.pos} zoom={c.zoom} file={c.f} tc={c.tc} dur={c.dur} delay={2900 + i * 90} />
            ))}
          </div>
        </ResultGroup>
      </AppFrame>
    </div>
  );
}

export function DesktopApp() {
  const feats: { icon: IconName; title: string; text: string }[] = [
    { icon: 'layers', title: 'Your whole library, no NLE required', text: 'Browse, preview and search every indexed drive from one window — even with Premiere closed.' },
    { icon: 'zap', title: 'Indexes in the background', text: 'Plug in a new drive and keep working. ClipRadar analyzes it quietly on your GPU.' },
    { icon: 'arrowRight', title: 'Send anywhere', text: 'Found it? Send it to your open Premiere project, copy the file path, or just drag and drop it — anywhere.' },
  ];
  // polish layer: scroll entrance + product-shot tilt on the app window
  const ref = useRef<HTMLElement | null>(null);
  const mockRef = useRef<HTMLDivElement | null>(null);
  useProductTilt(mockRef, 4, ref);
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: { trigger: ref.current, start: 'top 72%', once: true },
      });
      tl.from('.crwda-mock', { opacity: 0, y: 34, duration: 0.85 }, 0)
        .from('.crwda-row', { opacity: 0, x: 26, duration: 0.65, stagger: 0.12 }, 0.15);
    }, ref);
    return () => ctx.revert();
  }, []);
  return (
    <section ref={ref} style={{ padding: '90px 24px', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '10%', top: '30%', width: 800, height: 700, background: 'radial-gradient(ellipse, rgba(var(--light-rgb),0.08), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
        <SectionHead label="Desktop app" title="Power it up outside the NLE." sub="The companion desktop app is mission control for your footage — index, browse and search your entire library without opening a project." />
        <div className="crw-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 40, alignItems: 'center' }}>
          <div className="crwda-mock" ref={mockRef} style={{ minWidth: 0, willChange: 'transform' }}>
            <DesktopAppMock />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
            {feats.map((f) => (
              <div key={f.title} className="crwda-row" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <NeutralTile d={f.icon} size={42} />
                <div>
                  <b style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--fg-1)' }}>{f.title}</b>
                  <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.65, color: 'var(--fg-3)' }}>{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- WHY CLIPRADAR — asymmetric bento ---------- */
export function WhyBento() {
  const cell = (extra?: CSSProperties): CSSProperties => ({ borderRadius: 16, padding: 22, display: 'flex', flexDirection: 'column', gap: 9, position: 'relative', overflow: 'hidden', boxSizing: 'border-box', ...extra });
  const big: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--fg-1)' };
  const cap: CSSProperties = { fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)' };
  const txt: CSSProperties = { margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--fg-3)' };
  const stat: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.07em', color: 'var(--fg-4)', textTransform: 'uppercase' };
  const minis: { icon: IconName; t: string; s: string; m: string }[] = [
    { icon: 'cpu', t: 'GPU-accelerated', s: 'RTX eats terabytes overnight. Apple Silicon next.', m: 'NVIDIA · CUDA 12' },
    { icon: 'globe', t: '110+ languages', s: 'Japanese, Arabic, Swiss German dialects — search each in its own words.', m: 'word-level timestamps' },
    { icon: 'users', t: 'People collections', s: 'Name a face once. Every appearance, every drive, forever after.', m: '742 faces · 119 clusters' },
    { icon: 'film', t: 'Any format', s: 'H.264 to ProRes RAW. If your machine plays it, ClipRadar indexes it.', m: 'incl. audio-only WAV/MP3' },
  ];
  return (
    <section style={{ padding: '90px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="Why ClipRadar" title="Built like an instrument. Own it, don't rent it." />
        <div className="crw-bento" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'auto auto auto', gap: 12 }}>

          {/* LOCAL — hero cell, 2x2 */}
          <div className="crw-glass crw-gradborder" style={cell({ gridColumn: 'span 2', gridRow: 'span 2', padding: 28, justifyContent: 'center' })}>
            <span style={{ position: 'absolute', right: -50, top: -50, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05), transparent 70%)' }}></span>
            <NeutralTile d="lock" size={44} />
            <span className="crw-h-grad" style={{ ...big, fontSize: 58, marginTop: 6 }}>100% local</span>
            <p style={{ ...txt, fontSize: 14, maxWidth: '40ch' }}>Nothing uploads — not a frame, not a transcript. Analysis runs on your GPU, the index lives on your disk. Unplug the internet and search anyway.</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {['0 bytes uploaded', 'works fully offline', 'NDA-safe by design'].map((s) => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-2)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line-1)', padding: '4px 10px', borderRadius: 999 }}>
                  <span style={{ color: 'var(--fg-3)', display: 'inline-flex' }}><CRWIcon d="check" size={9} sw={3} /></span>{s}
                </span>
              ))}
            </div>
          </div>

          {/* FAST — wide, the race runs itself */}
          <div className="crw-glass crw-gradborder" style={cell({ gridColumn: 'span 2' })}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <NeutralTile d="zap" size={36} />
              <b style={cap}>Fast across terabytes</b>
              <span style={{ flex: 1 }}></span>
              <span className="crw-shimmer" style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600 }}>0.4s</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...stat, width: 86, flex: 'none' }}>ClipRadar</span>
              <span style={{ flex: 1, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}><i className="crw-bar" style={{ display: 'block', height: '100%', width: '4%', borderRadius: 3, background: 'var(--grad-accent)', transformOrigin: 'left', animationDelay: '400ms', animationDuration: '500ms' }}></i></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...stat, width: 86, flex: 'none' }}>Scrubbing</span>
              <span style={{ flex: 1, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}><i className="crw-bar" style={{ display: 'block', height: '100%', width: '96%', borderRadius: 3, background: 'rgba(255,255,255,0.14)', transformOrigin: 'left', animationDelay: '400ms', animationDuration: '3200ms' }}></i></span>
              <span style={stat}>2 days</span>
            </div>
            <p style={txt}>8.2 TB across nine drives, searched in under half a second.</p>
          </div>

          {/* UNLIMITED — boxed icon + icon-left header, same grammar as the minis */}
          <div className="crw-glass crw-gradborder" style={cell()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NeutralTile d="infinity" size={32} />
              <b style={cap}>Unlimited everything</b>
            </div>
            <p style={txt}>Searches, library size, analysis. No meters, no credits, no surprises.</p>
            <span style={{ ...stat, marginTop: 'auto' }}>flat. forever.</span>
          </div>

          {/* ULTRA SEARCH */}
          <div className="crw-glass crw-gradborder" style={cell()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NeutralTile d="brain" size={32} />
              <b style={cap}>Ultra Search built in</b>
            </div>
            <p style={txt}>One sentence in, every search mode out — fused into a single best-first list.</p>
            <span style={{ ...stat, marginTop: 'auto' }}>runs on your GPU · included</span>
          </div>

          {/* minis row */}
          {minis.map((m) => (
            <div key={m.t} className="crw-glass crw-card-hover crw-gradborder" style={cell({ padding: '18px 18px 16px' })}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <NeutralTile d={m.icon} size={32} />
                <b style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' }}>{m.t}</b>
              </div>
              <p style={txt}>{m.s}</p>
              <span style={{ ...stat, marginTop: 'auto' }}>{m.m}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
